import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { startConnectLogin } from '../lib/connectLogin';
import { MOCK_CONTACT } from '../mockData';
import { displaySummary } from '../lib/summary';
import { wrapNotesSeed } from '../lib/wrapupNotes';
import { apiHeaders } from '../lib/consoleApi';
import { declineAndReoffer } from '../lib/declineReoffer';   // 2026-10-13 decline re-offer
import { typeOfContact } from '../lib/railTab';
import { mapChannel } from '../lib/connectData';
import { findCallbackTask, taskWrapPayload, endsCallbackTask } from '../lib/oneWrapUp';
import { contactStillPresent } from '../lib/wrapUpComplete';   // 16 Oct: one wrap-up per callback   // 16 Oct: queue_callback is voice
import { notesKeyFor, readNotes, writeNotes, removeNotes } from '../lib/notesStore';
import { toRecord as softphoneRecord, remember as rememberSoftphoneError } from '../lib/softphoneErrors';
import { contactIdForApi, chainRootId, notesContactId, initiationMethodOf } from '../lib/contactIds';
import { isOutboundContact, callTransition, dialBlockedReason, ACCEPT_FAIL_GRACE_MS, acceptFailureVerdict,
         markIntentionalEnd, wasIntentionalEnd, contactErrorDetail, formatErrorDetail,
         markRingStart, clearRingStart, classifyContactError, wrapUpBlockedReason } from '../lib/callState';   // D37 + D39 + ring-out (2026-09-23)
import { ACW_ALERT_MINUTES, acwStartedAtFor } from '../lib/acw';
import { lastContactTenantAfter } from '../lib/dialerTenant';
import { queueMetaFrom } from '../lib/callbackOwner';   // Option B (2026-09-23)
import { callbackOriginFrom, dialContextMatches } from '../lib/callbackOrigin';   // #20 (2026-09-23)

const CCP_URL =
  import.meta.env.VITE_CCP_URL || 'https://knktr-demo.my.connect.aws/ccp-v2';

// Function URL of the knktr-agent-wrapup Lambda. Set VITE_WRAPUP_URL in .env
// before `vite build` for live persistence. When unset, Save & Complete fails
// with a clear message rather than silently completing the contact.
const WRAPUP_URL = import.meta.env.VITE_WRAPUP_URL || '';

// JWT-authorised preparation endpoint for agent-initiated outbound calls.
// It binds the selected tenant to this Streams agent + destination for the
// outbound whisper flow to consume before Connect places the customer call.
const OUTBOUND_CONTEXT_URL = import.meta.env.VITE_OUTBOUND_CONTEXT_URL || '';

// Row 1 Transfer, Phase 1: the console must never call contact.transfer()
// without a prior allow from this Lambda — it is the only thing standing
// between an agent's console and the tenant silo, since Connect itself
// enforces nothing ARN-scoped on transfer (see lambdas/knktr-transfer-prepare
// README). Queue and phone-number targets only in Phase 1; the agent target
// type is built server-side but not yet reachable from any console UI.
const TRANSFER_PREPARE_URL = import.meta.env.VITE_TRANSFER_PREPARE_URL || '';

// Row 1 Transfer, Phase 1 (2026-09-28): live-call testing found contact.transfer()
// fails on voice with "channel type unsupported for this operation" — confirmed
// against AWS's own TransferContact API reference ("Transfer is only supported
// for TASK and EMAIL contacts"). The real voice mechanism (AWS blog) is
// contact.addConnection(endpoint) to bring the target on, then
// contact.getAgentConnection().destroy() to drop the agent's own leg — two
// independent Streams calls, each with its own success/failure/silence outcome.
// This is how long handleTransfer waits for either call's callback before
// treating it as an unknown-state timeout rather than a clean failure.
// Overridable (VITE_TRANSFER_STEP_TIMEOUT_MS) only so scripts/verify-transfer-
// panel.mjs can shrink it for the timeout tests — never set in production.
const TRANSFER_STEP_TIMEOUT_MS = Number(import.meta.env.VITE_TRANSFER_STEP_TIMEOUT_MS) || 12000;

// D27: how long the status control waits for Connect to confirm a requested
// state before reading the snapshot itself and reporting a mismatch. Streams
// refreshes the agent snapshot every few seconds, so this is generous.
const STATUS_SETTLE_MS = 8000;

// Streams failure callbacks receive either a JSON string or an object; pull
// a human-readable reason out of either for the status-pill error line.
function describeStreamsError(err) {
  if (!err) return 'no reason given';
  if (typeof err === 'string') {
    try { const o = JSON.parse(err); return o?.message ?? o?.type ?? err; } catch { return err; }
  }
  return err.message ?? err.type ?? JSON.stringify(err);
}

// Incident 2026-09-14 fix 1: the display object was inline in onConnected;
// it is hoisted here so the onRefresh reconciler can rebuild the workspace
// for a contact that is still connected after the console cleared it
// (e.g. a voice leg dialled FROM a callback task has ended). Body unchanged
// apart from the two additions marked 2026-09-14.
function displayFromContact(contact, agent = null, dialCtx = null) {
  const attrs = contact.getAttributes();
  // Option B (2026-09-23, vault #234): the agent's own queue has no name in
  // Streams; on a personal callback task the card says "Your queue"
  // (lib/callbackOwner.js queueMetaFrom reads the queue, the channel and the
  // agent's user id from the Streams objects).
  return {
    // #20 (2026-09-23): the callback this leg was dialled for, if any — from
    // the stamped attributes and/or the dial context (see lib/callbackOrigin).
    callbackOrigin: callbackOriginFrom({ attrs, dialCtx }),
    // #24 (2026-09-23): how many bookings deep this contact's chain already
    // is — 0 if this contact was never itself part of one. Stamped by
    // knktr-callback-booking on every task it creates (and, via #20, on a
    // voice leg dialled from one); read-only display, the real gate is
    // server-side (lib/callback.mjs assessChainDepth in the wrap-up Lambda).
    chainDepth: Number.parseInt(attrs.chainDepth?.value, 10) || 0,
    brandName: attrs.brandName?.value ?? null,
    // 2026-09-14 fix 1: tenant + callback identity for dialling from an
    // accepted callback TASK (the D8 dial context). Null-safe on every channel.
    tenantId: attrs.tenantId?.value ?? null,
    callback: {
      callbackId: attrs.callbackId?.value ?? null,
      originalContactId: attrs.originalContactId?.value ?? null,
      callerNumber: attrs.callerNumber?.value ?? null,
    },
    // Tenant KB id for the agent copilot (D10). Set by the tenant router
    // as a contact attribute; null-safe so the copilot degrades to a
    // clear "no KB for this contact" state rather than erroring.
    knowledgeBaseId: attrs.knowledgeBaseId?.value ?? null,
    // Escalation context handed over from the AI leg. These attributes
    // are already set upstream by the tenant router / RAG Lambdas — we
    // only read them here; no flow change. Every field is null-safe so
    // the panel simply omits anything not present on a given contact.
    escalation: {
      brandName: attrs.brandName?.value ?? null,
      reason: attrs.ai_escalation_reason?.value ?? null,
      lastQuestion: attrs.ai_last_question?.value ?? null,
      lastAnswer: attrs.ai_last_answer?.value ?? null,
      // v9 escalation context (2026-09-10): set by the RAG Lambda on
      // every escalation path and mapped by the flow. Absent on
      // contacts escalated before the flow change and on tasks — the
      // consumers fall back to the legacy fields when `trigger` is null.
      trigger: attrs.escalationTrigger?.value || null,
      handoverQuestion: attrs.handoverQuestion?.value || null,
    },
    customer: {
      name: attrs.customerName?.value ?? null,
      account: attrs.accountId?.value ?? null,
      plan: attrs.plan?.value ?? null,
      phone: contact.getInitialConnection()?.getEndpoint()?.phoneNumber ?? attrs.callerNumber?.value ?? '',
    },
    meta: {
      // D31 (2026-09-14): the LIVE leg's id — on a re-offered callback
      // getInitialContactId() is the chain root, which is a different
      // contact with no agent on it. The root is kept alongside for
      // display only. See lib/contactIds.js.
      contactId: contactIdForApi(contact),
      initialContactId: chainRootId(contact),
      // Rows 46/48: what kind of linked leg this is (transfer vs re-offered callback).
      initiationMethod: initiationMethodOf(contact),
      queue: queueMetaFrom(contact, agent, attrs.brandName?.value ?? null),
      startTime: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      // Flow writes tenantDnis; older attribute name `dnis` kept as a fallback.
      dnis: attrs.tenantDnis?.value ?? attrs.dnis?.value ?? '—',
      channel: mapChannel(contact.getType()),   // queue_callback → VOICE (16 Oct)
    },
    aiHandled: attrs.aiHandled?.value === 'true',
    aiHandledSeconds: Number(attrs.aiHandledSeconds?.value ?? 0),
    // 2026-10-13: the model summary lives in aiSummaryFull (the flow overwrites aiSummary).
    aiSummary: displaySummary(attrs.aiSummaryFull?.value || attrs.aiSummary?.value),
    intents: attrs.detectedIntents?.value
      ? attrs.detectedIntents.value.split(',').map(s => s.trim()).filter(Boolean)
      : [],
    sentiment: null,
    transcript: [],
    contextAlerts: [],
    suggestedResponses: [],
    kbArticles: [],
    piiDetected: null,
    // Scheduled-callback context (present on callback tasks only).
    callbackWindow: attrs.callbackWindow?.value ?? null,
    // Prior after-hours attempt, stamped by the inbound flow's lookup.
    prior: attrs.priorAttempt?.value === 'true'
      ? {
          at: attrs.priorAttemptAt?.value ?? null,
          summary: attrs.priorSummary?.value ?? null,
          count: attrs.priorCount?.value ?? null,
        }
      : null,
  };
}
// D32 (2026-09-14): the wrap-up context was built inline in onACW; hoisted so
// the onRefresh reconciler can rebuild it for a contact found already in ACW
// (after a page reload) — presentation of an existing state, not remediation.
function channelOf(contact) { try { return String(contact?.getType?.() || '').toLowerCase(); } catch { return ''; } }
function wrapContextFrom(contact, dialCtx = null, now = Date.now()) {
  const attrs = contact.getAttributes();
  return {
    // D31 (2026-09-14): the wrap-up API binds on DescribeContact's
    // AgentInfo, which only the CONNECTED leg carries. On a bounced /
    // re-offered queued callback the old expression here
    // (getInitialContactId() || getContactId()) sent the chain ROOT —
    // a different, never-connected contact — and every save was
    // refused "no agent on contact" (seen live 11:40 today on
    // cca2a588 → 2cc441e3 → 364a2eda → 85db4f8c). Live leg first;
    // the root only as the D8 fallback when Streams has no own id.
    contactId: contactIdForApi(contact),
    // Chain root, for display and future history linkage only. The
    // backend ignores fields it does not know.
    initialContactId: chainRootId(contact),
    initiationMethod: initiationMethodOf(contact),
    tenantId:
      attrs.tenantId?.value
      ?? dialCtx?.tenantId
      // D26 (2026-09-18): no build-time VITE_TENANT_ID fallback. A contact with
      // no tenant must be refused by the save guard below, not saved against
      // whichever tenant the build happened to carry.
      ?? null,
    brandName: attrs.brandName?.value ?? dialCtx?.brandName ?? null,
    // 2026-10-13: the model summary lives in aiSummaryFull (the flow overwrites aiSummary).
    aiSummary: displaySummary(attrs.aiSummaryFull?.value || attrs.aiSummary?.value),
    // 2026-09-17: the callback this outbound leg was dialled FOR, if any
    // (CallbacksPanel's "Call now" hands it through dialCtx — D8's own
    // fallback path, since Streams dialling can't set contact attributes).
    // Was computed here all along but never actually forwarded past this
    // point — submitWrapUp dropped it before it reached the Lambda. See
    // docs/ops/callback-ownership-tasklist-investigation-2026-09-17.md.
    // #20 (2026-09-23): the stamped attribute now carries it on a console-
    // dialled voice leg, so a reload no longer loses it. Never from a TASK's
    // own callbackId attribute (that would run the connect-gap flag on task
    // timestamps).
    callbackId: dialCtx?.callbackId ?? (channelOf(contact) === 'task' ? null : (attrs.callbackId?.value ?? null)),
    // D32: when ACW began (Streams' ended-state timestamp, so it survives a
    // reload) — the wrap-up clock and the urgent threshold key off this.
    acwStartedAt: acwStartedAtFor(contact, now),
  };
}

const ContactContext = createContext(null);

// Demo/showcase mode: agents.knktr.ai/?demo=1 renders the seeded Sarah Mitchell
// contact without touching Amazon Connect. The default (live) view starts empty.
const DEMO =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('demo');
// Row 234 (2026-10-31): demo mode can open any console screen for review and
// demos without a live contact: &screen=call (default) | acw | idle | ringing
// | task. Demo mode never saves, rings or touches Connect (guards below).
const DEMO_SCREEN = DEMO
  ? (['call', 'acw', 'idle', 'ringing', 'task'].includes(new URLSearchParams(window.location.search).get('screen'))
    ? new URLSearchParams(window.location.search).get('screen') : 'call')
  : null;

const initialState = {
  agentStatus: DEMO ? 'Available' : 'Offline',
  // D27 (2026-09-10): outcome of the last state change the console itself
  // requested. pending = the name asked for, until Connect confirms it via
  // STATE_CHANGE (or the onRefresh reconciler catches up); error = a refusal
  // or a non-confirmation, shown beside the pill instead of only in devtools.
  agentStatusPending: null,
  agentStatusError: null,
  agentConnected: DEMO,        // true once a Connect agent has authenticated
  agent: null,                 // raw Streams agent object, needed for setState()
  contact: null,
  incomingContact: DEMO_SCREEN === 'ringing'
    ? { contact: null, caller: MOCK_CONTACT.customer.phone, tenant: MOCK_CONTACT.brandName }
    : null,                    // { contact, caller, tenant } while ringing, pre-accept
  callActive: false,
  isMuted: false,
  isHeld: false,
  // After Call Work (ACW) wrap-up gate. acwActive is true from contact.onACW
  // until a wrap-up is successfully saved; the contact is NOT cleared (and so
  // never completes Connect-side) while it is true. wrapContext carries the
  // identifiers the wrap-up write needs, captured independently of `display`
  // so it survives even if onEnded clears the panel first.
  acwActive: DEMO_SCREEN === 'acw',
  wrapContext: DEMO_SCREEN === 'acw'
    ? { tenantId: null, contactId: MOCK_CONTACT.meta.contactId, brandName: MOCK_CONTACT.brandName, aiSummary: MOCK_CONTACT.aiSummary, acwStartedAt: Date.now() }
    : null,                    // { tenantId, contactId, brandName, aiSummary }
  wrapSaving: false,
  wrapError: null,
  // D6/D19 (2026-09-09): contacts Streams reports in a state that holds the
  // agent non-routable (missed / error) but which no screen was showing.
  // [{ id, type, status, since }] — never the Streams object itself.
  stuckContacts: [],
  dialPending: false,
  dialError: null,
  // Row 1 Transfer, Phase 1: mirrors dialPending/dialError exactly.
  transferPending: false,
  transferError: null,
  // Set when addConnection() or destroy() failed/timed out AFTER the target
  // was (or may have been) connected — the call may still be silently joined
  // to a third party. Cleared ONLY by CONTACT_ENDED for this contact, never by
  // dismissing a banner and never implicitly by a later TRANSFER_PENDING — see
  // handleTransfer's own guard against a second attempt while this is true.
  transferPartial: false,
  // Set when either Streams call's callback never fired within the timeout —
  // genuinely unknown outcome, distinct from a confirmed clean failure.
  transferTimedOut: false,
  // D37 (2026-09-16): an outbound contact we placed that is ringing and not
  // yet answered. contact is set, callActive is NOT; End works, Mute/Hold do not.
  dialling: false,
  // D39 (2026-09-16): timestamp of a ringing call the agent cancelled, so the
  // idle screen can confirm it instead of showing nothing (or a false fault).
  callCancelledAt: null,
  // 2026-09-23 (ring-out banner): timestamp of an inbound ring that timed out
  // unanswered, so the idle screen says "missed" instead of showing a fault.
  callMissedAt: null,
  // Incident 2026-09-14 fix 1: channel of the connected contact ('voice' |
  // 'task' | 'chat'). callActive stays true for any connected contact (the
  // rails depend on it); dialling is blocked only while activeChannel is
  // 'voice' — Connect itself allows a voice call alongside a task.
  activeChannel: null,
  // Incident 2026-09-14 fix 2: latest Streams softphone error (or our own
  // accept/contact failure), shown by SoftphoneBanner until dismissed.
  softphoneError: null,
  // D28 (2026-09-10): live-call notes. Lifted here from RightRail's local
  // state because App unmounts RightRail on the ACW transition, which
  // destroyed whatever the agent had typed. Reset on a new contact and on a
  // saved wrap-up — NOT on CONTACT_ENDED, which fires around ACW. Mirrored to
  // sessionStorage under callNotesKey (see lib/notesStore.js).
  callNotes: '',
  callNotesKey: null,
  // 2026-09-15: true when the wrap-up notes were opened from the AI summary
  // (no live notes). Drives the AI-suggested cue, which persists until Save.
  wrapNotesAiSeeded: false,
  // 2026-09-16: outcome of the client alert on the last saved wrap-up —
  // { sent, error, recipient: 'tenant'|'fallback'|null } or null. The Lambda
  // has always returned it; the agent never saw it. Cleared on the next contact.
  wrapAlert: null,
  // 2026-09-17: outcome of an agent-scheduled callback on the last saved
  // wrap-up — { booked, windowText, callbackId, error } or null. Cleared on
  // the next contact.
  wrapCallback: null,
  // display — null until a live contact connects (or seeded in demo mode)
  display: DEMO_SCREEN === 'call' || DEMO_SCREEN === 'acw' ? MOCK_CONTACT : null,
  // Idle screen v3 (2026-09-19): the tenant of the contact this agent last
  // finished, taken from that contact's own tenantId attribute as its
  // workspace is cleared. Only the idle outbound dialer reads it, as a
  // pre-fill the agent can change (lib/dialerTenant.js). Never a build-time
  // default: null until a real contact with a tenant has been handled.
  lastContactTenantId: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'AGENT_STATUS': {
      // D27 (2026-09-10): Streams delivers STATE_CHANGE with newState as the
      // state NAME — a string. The handler used to read `.name` off it, got
      // undefined, and this `??` then silently kept the stale status: every
      // Connect state event was dropped, and only a page load ever read the
      // truth. The caller now passes the name itself; the `??` stays as a
      // genuine guard for a snapshot with no name, not as a mask.
      const status = action.status ?? state.agentStatus;
      const changed = status !== state.agentStatus;
      // The onRefresh reconciler ticks every few seconds with `reconcile`.
      // An unchanged tick must neither re-render nor clear a pending request
      // that Connect simply hasn't reflected in its snapshot yet.
      if (!changed && (action.reconcile || (!state.agentStatusPending && !state.agentStatusError))) {
        return state;
      }
      return { ...state, agentStatus: status, agentStatusPending: null, agentStatusError: null };
    }
    case 'AGENT_STATUS_PENDING':
      return { ...state, agentStatusPending: action.name, agentStatusError: null };
    case 'AGENT_STATUS_ERROR':
      return { ...state, agentStatusPending: null, agentStatusError: action.error };
    case 'AGENT_STATUS_SETTLE': {
      // Settle timer after a request: if STATE_CHANGE already confirmed it,
      // pending is null and there is nothing to do. Otherwise adopt what the
      // snapshot says and report the mismatch if it is not what was asked.
      if (!state.agentStatusPending) return state;
      const status = action.status ?? state.agentStatus;
      const ok = status === action.requested;
      return {
        ...state,
        agentStatus: status,
        agentStatusPending: null,
        agentStatusError: ok
          ? null
          : `Connect did not confirm ${action.requested} \u2014 it still reports ${status}. Try again.`,
      };
    }
    case 'AGENT_CONNECTED':
      return { ...state, agentConnected: action.value };
    case 'AGENT_OBJECT':
      return { ...state, agent: action.agent };
    case 'CONTACT_INCOMING':
      return {
        ...state,
        // A new contact supersedes any lingering ACW state from a prior call.
        acwActive: false,
        wrapContext: null,
        wrapSaving: false,
        wrapError: null,
        // D28: a new contact starts with empty notes.
        callNotes: '',
        callNotesKey: null,
        wrapNotesAiSeeded: false,
        callMissedAt: null,   // a new ring supersedes the last missed notice
        wrapAlert: null,
        wrapCallback: null,
        incomingContact: { contact: action.contact, caller: action.caller, tenant: action.tenant },
      };
    case 'CONTACT_INCOMING_CLEARED':
      return { ...state, incomingContact: null };
    case 'CALL_CANCELLED':
      return { ...state, callCancelledAt: action.at };
    case 'CALL_CANCELLED_CLEAR':
      return { ...state, callCancelledAt: null };
    case 'CALL_MISSED':
      return { ...state, callMissedAt: action.at };
    case 'CALL_MISSED_CLEAR':
      return { ...state, callMissedAt: null };
    case 'CONTACT_DIALLING':
      // D37: outbound leg placed, far end ringing. Ignored if a call is already
      // connected (see lib/callState.js).
      return { ...state, callCancelledAt: null, ...(callTransition(state, action) || {}) };
    case 'CONTACT_CONNECTED':
      return {
        ...state,
        incomingContact: null,
        ...callTransition(state, action),   // contact, callActive, dialling:false, channel, mute/hold reset (D37)
        // D28: key the notes mirror to this contact; restore anything a
        // previous page load of the same contact had stored.
        callNotesKey: action.notesKey ?? state.callNotesKey,
        callNotes: action.storedNotes ?? state.callNotes,
      };
    case 'CONTACT_ENDED':
      // Clear display too — otherwise the shell keeps rendering the ended
      // contact's panels instead of falling back to WaitingForContact.
      // Note: this does NOT clear acwActive/wrapContext. The ACW gate is
      // released only by a successful save (ACW_COMPLETE) or by a fresh
      // incoming contact (CONTACT_INCOMING). onEnded fires after our own
      // clear() on save, so by then acwActive is already false.
      return {
        ...state,
        incomingContact: null,
        ...callTransition(state, action),   // contact null, callActive/dialling false (D37)
        lastContactTenantId: lastContactTenantAfter(state.display, state.lastContactTenantId),
        display: null,
        // Row 1 Transfer: this is the ONLY place transferPartial/transferTimedOut
        // clear (approved design, 2026-09-28) — the contact this ambiguity was
        // about has now actually ended, so it is resolved one way or the other.
        transferPending: false,
        transferError: null,
        transferPartial: false,
        transferTimedOut: false,
        softphoneError:
          state.softphoneError?.type === 'transfer_partial' || state.softphoneError?.type === 'transfer_timeout'
            ? null
            : state.softphoneError,
      };
    case 'CONTACT_ACW':
      // Entered After Call Work. Show the wrap-up gate; do not clear the
      // contact (that would complete it Connect-side before a save).
      // D28: notes carry across untouched. If the page was reloaded into ACW
      // (no CONTACT_CONNECTED this load), fall back to the stored mirror.
      // 2026-09-15: if both are empty and the bot leg left an aiSummary, open
      // the wrap-up with that (flagged on screen as AI-suggested). The agent's
      // own words always win; contacts with no summary are unchanged.
      {
        const seed = wrapNotesSeed({
          liveNotes: state.callNotes,
          storedNotes: action.storedNotes,
          aiSummary: action.wrapContext?.aiSummary,
        });
        return {
          ...state,
          acwActive: true,
          wrapContext: action.wrapContext,
          callNotesKey: state.callNotesKey ?? action.notesKey ?? null,
          callNotes: seed.notes,
          wrapNotesAiSeeded: seed.aiSeeded,
        };
      }
    case 'WRAP_ALERT':
      return { ...state, wrapAlert: action.alert };
    case 'WRAP_CALLBACK':
      return { ...state, wrapCallback: action.callback };
    case 'WRAP_SAVING':
      return { ...state, wrapSaving: true, wrapError: null };
    case 'WRAP_ERROR':
      return { ...state, wrapSaving: false, wrapError: action.error };
    case 'ACW_COMPLETE':
      // Wrap-up saved successfully — release the gate and reset the panels.
      return {
        ...state,
        acwActive: false,
        wrapContext: null,
        wrapSaving: false,
        wrapError: null,
        lastContactTenantId: lastContactTenantAfter(state.display, state.lastContactTenantId),
        display: null,
        // D28: the wrap-up is saved; the notes went with it.
        callNotes: '',
        callNotesKey: null,
        wrapNotesAiSeeded: false,
      };
    case 'SET_CALL_NOTES':
      return { ...state, callNotes: action.text };
    case 'SET_MUTED':
      return { ...state, isMuted: action.value };
    case 'SET_HELD':
      return { ...state, isHeld: action.value };
    case 'SET_DISPLAY':
      return { ...state, display: action.data };
    case 'SET_DISPLAY_SUMMARY':
      return state.display ? { ...state, display: { ...state.display, aiSummary: action.aiSummary } } : state;
    case 'STUCK_CONTACTS':
      return { ...state, stuckContacts: action.list };
    case 'DIAL_PENDING':
      return { ...state, dialPending: true, dialError: null };
    case 'DIAL_READY':
      return { ...state, dialPending: false, dialError: null };
    case 'DIAL_ERROR':
      return { ...state, dialPending: false, dialError: action.error };
    case 'TRANSFER_PENDING':
      return { ...state, transferPending: true, transferError: null };
    case 'TRANSFER_READY':
      return { ...state, transferPending: false, transferError: null };
    case 'TRANSFER_ERROR':
      // partial/timedOut default false — every ordinary "could not even start"
      // failure (not configured, prepare rejected, no contact) is a clean,
      // known-safe no-op. Only the addConnection/destroy outcomes below the
      // prepare call ever pass partial/timedOut true.
      return {
        ...state,
        transferPending: false,
        transferError: action.error,
        transferPartial: !!action.partial,
        transferTimedOut: !!action.timedOut,
      };
    case 'SOFTPHONE_ERROR':
      return { ...state, softphoneError: action.record };
    case 'SOFTPHONE_ERROR_CLEAR':
      // Row 1 Transfer: a transfer_partial/transfer_timeout record means this
      // contact may still be silently joined to a third party — dismissing the
      // banner must not hide that. It clears only via CONTACT_ENDED, above.
      if (state.softphoneError?.type === 'transfer_partial' || state.softphoneError?.type === 'transfer_timeout') {
        return state;
      }
      return { ...state, softphoneError: null };
    default:
      return state;
  }
}

export function ContactProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  // Latest state for callbacks that live outside React's render (the
  // onRefresh reconciler) — incident 2026-09-14 fix 1.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Incident 2026-09-14 fix 2: one funnel for every softphone / CCP failure.
  // Streams only publishes these on its event bus; until now nothing here
  // listened, so a voice accept whose media never came up looked like
  // nothing happened. Each report is dispatched to the banner, logged with a
  // stable prefix, and kept in sessionStorage (last 20) for after-the-fact
  // diagnosis. This surfaces the failure; it does not try to work around it.
  const reportSoftphone = useCallback((type, err) => {
    const rec = softphoneRecord(type, err);
    console.error('[knktr softphone]', rec.type, rec.detail || '', err || '');
    rememberSoftphoneError(rec);
    dispatch({ type: 'SOFTPHONE_ERROR', record: rec });
  }, []);
  const clearSoftphoneError = useCallback(() => dispatch({ type: 'SOFTPHONE_ERROR_CLEAR' }), []);
  const clearCallCancelled = useCallback(() => dispatch({ type: 'CALL_CANCELLED_CLEAR' }), []);   // D39
  const clearCallMissed = useCallback(() => dispatch({ type: 'CALL_MISSED_CLEAR' }), []);         // ring-out (2026-09-23)
  const ccpContainerRef = useRef(null);
  // D21: contactIds already wired / already announced, so the event path and
  // the reconciler below can both run without double-dispatching.
  const wiredRef = useRef(new Set());
  const announcedRef = useRef(new Set());
  const diallingRef = useRef(new Set());   // D37: contact ids recorded as dialling
  // D39: contacts WE ended, id → timestamp, so onError can tell our own
  // hang-up from a real fault; and which destroy path actually ran.
  const intentionalEndRef = useRef(new Map());
  // 2026-09-23 (ring-out banner): when each inbound ring was announced, and
  // which contacts the agent pressed Accept on — contact.onError reads both
  // (lib/callState.js classifyContactError) to tell a ring that timed out
  // unanswered from a genuine fault. Streams emits ERROR (then MISSED, ENDED)
  // before DESTROYED and drops every per-contact handler at destroy, so the
  // entries are consumed by onError, cleared on connect, or pruned in
  // onDestroy — whichever comes first. (D39's "the ERROR follows the destroy"
  // is about the agentConnection.destroy() API call, not contact.onDestroy.)
  const ringStartRef = useRef(new Map());
  const acceptAttemptRef = useRef(new Set());
  const lastEndPathRef = useRef(null);
  // Signature of the last STUCK_CONTACTS dispatch, so onRefresh (every few
  // seconds) only re-renders when the stuck set actually changes.
  const stuckSigRef = useRef('');
  // Refs (not state) so the wrap-up save reads the current agent + ACW contact
  // without stale-closure risk and without re-subscribing Streams handlers.
  const agentRef = useRef(null);
  const acwContactRef = useRef(null);
  // One wrap-up per callback (16 Oct): tasks this console is closing together with their call,
  // taskContactId -> { voiceContactId, disposition, notes, started }.
  const autoWrapRef = useRef(new Map());
  // Context handed over by handleDialNumber for outbound callback legs, where
  // Streams cannot set contact attributes (D8 root cause). Read + cleared in
  // contact.onACW as the attribute fallback.
  const pendingDialContextRef = useRef(null);
  // D27: settle timer for the last requested state change.
  const settleTimerRef = useRef(null);
  // D28: current notes mirror key, readable from the wrap-up save without a
  // stale closure.
  const notesKeyRef = useRef(null);
  useEffect(() => { notesKeyRef.current = state.callNotesKey; }, [state.callNotesKey]);
  // D28: mirror the notes to sessionStorage whenever they change.
  useEffect(() => {
    if (state.callNotesKey) writeNotes(state.callNotesKey, state.callNotes);
  }, [state.callNotes, state.callNotesKey]);

  useEffect(() => {
    if (DEMO) return;                       // showcase mode: no Connect wiring
    const connectApi = window.connect;
    if (!ccpContainerRef.current || !connectApi) return;

    // loginPopup:false — we surface a click-driven login button in the logged-out
    // screen instead, so the popup is opened by a user gesture (never blocked).
    connectApi.core.initCCP(ccpContainerRef.current, {
      ccpUrl: CCP_URL,
      loginPopup: false,
      softphone: { allowFramedSoftphone: true, disableRingtone: false },
      ccpAckTimeout: 5000,
      ccpSynTimeout: 3000,
    });

    connectApi.core.onAuthFail(() =>
      dispatch({ type: 'AGENT_CONNECTED', value: false })
    );
    // Incident 2026-09-14 fix 2: Connect refusing the console outright.
    if (typeof connectApi.core.onAccessDenied === 'function') {
      connectApi.core.onAccessDenied(() => reportSoftphone('access_denied'));
    }

    // Raises the accept screen for one contact. Hoisted out of wireContact so
    // the D21 reconciler can raise it for a contact whose onIncoming/
    // onConnecting never fired. announcedRef makes it idempotent across both
    // callers — announcing twice would re-render the accept screen mid-click.
    // D37: idempotent per contact (Streams fires onConnecting more than once and
    // the reconciler sees the same contact on every refresh).
    const markDialling = contact => {
      let id = null;
      try { id = contact.getContactId(); } catch { /* fall through */ }
      if (id && diallingRef.current.has(id)) return;
      let channel = 'voice';
      try { channel = mapChannel(contact.getType()).toLowerCase(); } catch { /* voice */ }
      dispatch({ type: 'CONTACT_DIALLING', contact, channel });
      if (id) diallingRef.current.add(id);
    };

    const announceFor = contact => {
      let id = null;
      try { id = contact.getContactId(); } catch { /* fall through */ }
      if (id && announcedRef.current.has(id)) return;
      // Outbound dials (Task "Call now") also fire onConnecting — they need
      // no accept screen; the workspace populates on onConnected instead.
      try { if (contact.isInbound && !contact.isInbound()) return; } catch { /* treat as inbound */ }
      const attrs = contact.getAttributes();
      // D21 (2026-09-08): on a task contact getInitialConnection() throws if
      // Streams cannot build the task media controller — which it could not
      // for a week, because amazon-connect-taskjs was never loaded (main.jsx).
      // Guard it like isInbound() so a throw degrades to "Unknown number",
      // never to a missing accept screen.
      let phone = null;
      try { phone = contact.getInitialConnection()?.getEndpoint()?.phoneNumber ?? null; } catch { /* no endpoint */ }
      if (id) markRingStart(ringStartRef.current, id);   // ring-out (2026-09-23)
      dispatch({
        type: 'CONTACT_INCOMING',
        contact,
        caller: phone || attrs.callerNumber?.value || 'Unknown number',
        tenant: attrs.brandName?.value ?? attrs.tenantId?.value ?? 'Unknown tenant',
      });
      // Mark announced only once the accept screen is actually up. Marking
      // before the dispatch let a throw above poison the guard for good — the
      // reconciler then skipped the contact on every later tick while it rang.
      if (id) announcedRef.current.add(id);
    };

    // D21 (2026-09-08): wireContact is called from TWO places — Streams'
    // connect.contact() subscription AND the agent.onRefresh reconciler below.
    // wiredRef makes it idempotent: subscribing the same handler twice would
    // double-dispatch every event.
    const wireContact = contact => {
      const wireId = (() => { try { return contact.getContactId(); } catch { return null; } })();
      if (wireId && wiredRef.current.has(wireId)) return;
      if (wireId) wiredRef.current.add(wireId);
      // D21 (2026-09-08): Streams drops EVERY per-contact subscription on
      // contact::destroyed (measured: all zero afterwards) and, if the same
      // contact is re-offered — e.g. a missed task — re-inits a NEW Contact
      // object and fires connect.contact() again. Prune both guards here so
      // that re-offer is wired and announced afresh. Double-subscription is
      // not a risk: by the time this fires Streams has already unsubscribed
      // everything for this id, including our onConnected/onACW.
      contact.onDestroy(() => {
        if (!wireId) return;
        wiredRef.current.delete(wireId);
        announcedRef.current.delete(wireId);
        diallingRef.current.delete(wireId);   // D37
        ringStartRef.current.delete(wireId);      // ring-out (2026-09-23)
        acceptAttemptRef.current.delete(wireId);  // ring-out (2026-09-23)
      });
      // Streams event semantics (confirmed against docs + issues #85/#218):
      // inbound voice fires onConnecting (onIncoming NEVER fires for inbound);
      // queued callbacks fire onIncoming. Subscribe both to one handler.
      // CAVEAT for when the callback dialer goes live: for callbacks,
      // onConnecting re-fires AFTER accept — add a state guard then.
      contact.onIncoming(() => announceFor(contact));
      // D37 (2026-09-16): an OUTBOUND contact's onConnecting is the far end
      // ringing. Record it as dialling so End can drop it before it is
      // answered; announceFor() would (rightly) do nothing for it.
      contact.onConnecting(() => {
        if (isOutboundContact(contact)) { markDialling(contact); return; }
        announceFor(contact);
      });
      // Incident 2026-09-14 fix 2: a contact Connect puts into ERROR on this
      // session (e.g. the caller hung up mid-ring, or a failed connection).
      contact.onError(() => {
        // D39 (2026-09-16): Streams fires ERROR for `connecting → error`, which
        // is exactly what cancelling a ringing outbound call produces. If we
        // ended THIS contact ourselves in the last few seconds, that is our own
        // action, not a softphone fault — confirm the cancel instead. Every
        // other ERROR still banners, including a second one for the same
        // contact (wasIntentionalEnd consumes the entry).
        const detail = contactErrorDetail(contact);
        const outcome = classifyContactError({
          intentionalEnds: intentionalEndRef.current,
          ringStarts: ringStartRef.current,
          acceptAttempts: acceptAttemptRef.current,
          contactId: detail.contactId,
        });
        if (outcome === 'cancelled') {
          console.info(JSON.stringify({ msg: 'call-cancelled', ...detail, endPath: lastEndPathRef.current }));
          dispatch({ type: 'CALL_CANCELLED', at: Date.now() });
          return;
        }
        // 2026-09-23 (ring-out banner): an inbound ring that timed out unanswered
        // is a MISSED contact, not a fault. Same transition as D39, different
        // cause; see lib/callState.js for the (narrow) rule. onMissed still
        // clears the workspace; this only decides what the idle screen says.
        if (outcome === 'missed') {
          console.info(JSON.stringify({ msg: 'call-missed', ...detail }));
          dispatch({ type: 'CALL_MISSED', at: Date.now() });
          return;
        }
        reportSoftphone('contact_error', { errorMessage: formatErrorDetail(detail) });
      });

      contact.onConnected(() => {
        // ring-out (2026-09-23): a connected contact is no longer a ring.
        if (wireId) { clearRingStart(ringStartRef.current, wireId); acceptAttemptRef.current.delete(wireId); }
        // D28: key the notes mirror to the contact and restore any stored text
        // (a refresh mid-call reloads the page but not React state).
        // D31 (2026-09-14): notes are keyed on the chain ROOT on purpose, so
        // text typed during a bounced callback offer survives to the leg that
        // finally connects. See lib/contactIds.js for the id rules.
        const notesKey = notesKeyFor(notesContactId(contact));
        // Incident 2026-09-14 fix 1: record the channel so a connected TASK
        // does not block dialling (only a live VOICE leg does).
        let channel = null;
        try { channel = mapChannel(contact.getType()).toLowerCase(); } catch { /* unknown */ }
        dispatch({ type: 'CONTACT_CONNECTED', contact, channel, notesKey, storedNotes: readNotes(notesKey) });

        // Populate display data strictly from real contact attributes set by the
        // tenant router / RAG Lambdas. No mock fallback in live mode — panels
        // for anything not yet wired (transcript, AI Assist, history, customer
        // profile) get an honest empty/"coming soon" state, never fabricated data.
        // Real-time transcript comes from Contact Lens streaming (wire separately via WebSocket).
        // #20: an OUTBOUND leg placed from a console dial carries the dial context
        // ONLY when the contact's outboundContextId is the one that dial prepared
        // (review 2026-09-23: a stale ref must never paint another customer's
        // callback onto an unrelated outbound call). Read, not consumed — onACW
        // consumes it for the wrap-up.
        dispatch({ type: 'SET_DISPLAY', data: displayFromContact(contact, agentRef.current, dialContextMatches(pendingDialContextRef.current, contact.getAttributes()) ? pendingDialContextRef.current : null) });
      });

      contact.onEnded(() => dispatch({ type: 'CONTACT_ENDED' }));
      contact.onMissed(() => dispatch({ type: 'CONTACT_ENDED' }));
      // After Call Work is now GATED. Previously onACW called contact.clear()
      // immediately, which completed the contact Connect-side with no wrap-up.
      // We now hold the contact open, show the wrap-up panel, and clear() only
      // after a successful save (see submitWrapUp -> ACW_COMPLETE). Capture the
      // contact + its identifiers here so the save has them even if onEnded
      // clears `display` first.
      contact.onACW(() => {
        // One wrap-up per callback (16 Oct): the task of a callback whose call was just wrapped up
        // is saved and completed here, with the call's outcome, instead of asking the agent again.
        // Any failure falls back to the normal wrap-up screen, so nothing is left stuck.
        let ownId = null; try { ownId = contact.getContactId(); } catch { /* none */ }
        const auto = ownId ? autoWrapRef.current.get(ownId) : null;
        if (auto && !auto.started) {
          auto.started = true;
          (async () => {
            try {
              const taskCtx = wrapContextFrom(contact);
              if (!WRAPUP_URL || !taskCtx.contactId || !taskCtx.tenantId) throw new Error('missing endpoint or ids');
              const res = await fetch(WRAPUP_URL, {
                method: 'POST',
                headers: await apiHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(taskWrapPayload({ taskCtx, voiceContactId: auto.voiceContactId, disposition: auto.disposition, notes: auto.notes,
                  agentUsername: agentRef.current?.getConfiguration?.()?.username })),
              });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              await new Promise((ok, no) => contact.clear({ success: ok, failure: no }));
              autoWrapRef.current.delete(ownId);
              console.info('[one wrap-up] callback task closed with its call', ownId);
            } catch (err) {
              console.warn('[one wrap-up] task not closed automatically; showing its wrap-up', err);
              autoWrapRef.current.delete(ownId);
              acwContactRef.current = contact;
              const nk = notesKeyFor(notesContactId(contact));
              dispatch({ type: 'CONTACT_ACW', notesKey: nk, storedNotes: auto.notes || readNotes(nk), wrapContext: wrapContextFrom(contact) });
            }
          })();
          return;
        }
        acwContactRef.current = contact;
        // D8: outbound legs dialled via Streams carry no attributes — fall
        // back to the context the dial click handed over (consumed once).
        // #20 (review 2026-09-23): the ref is consumed by whichever contact
        // reaches ACW next, but USED only if it is the contact that dial
        // prepared (outboundContextId == requestId). A stale ref is dropped.
        const pendingRef = pendingDialContextRef.current;
        pendingDialContextRef.current = null;
        let acwAttrs = {}; try { acwAttrs = contact.getAttributes() || {}; } catch { /* none */ }
        const dialCtx = dialContextMatches(pendingRef, acwAttrs) ? pendingRef : null;
        const notesKey = notesKeyFor(notesContactId(contact));   // D31: chain root, see onConnected
        dispatch({
          type: 'CONTACT_ACW',
          notesKey,
          storedNotes: readNotes(notesKey),
          wrapContext: wrapContextFrom(contact, dialCtx),   // D31 ids + D32 acwStartedAt inside
        });
      });
    };

    connectApi.contact(wireContact);

    connectApi.agent(agent => {
      agentRef.current = agent;   // used by the wrap-up save for agentUsername
      dispatch({ type: 'AGENT_CONNECTED', value: true });
      dispatch({ type: 'AGENT_OBJECT', agent });
      dispatch({ type: 'AGENT_STATUS', status: agent.getState().name });
      // D27 (2026-09-10): newState IS the state name — a string, per Streams
      // (release/connect-streams.js STATE_CHANGE emit; index.d.ts
      // AgentStateChange.newState: string). `newState.name` was undefined and
      // every event was dropped. Tolerate an object too, in case a future
      // Streams version changes the payload shape.
      agent.onStateChange(({ newState }) =>
        dispatch({
          type: 'AGENT_STATUS',
          status: typeof newState === 'string' ? newState : newState?.name,
        })
      );
      // Mute state truth comes from the MUTE_TOGGLE event —
      // agent.mute()/unmute() take no callbacks in Streams 2.x.
      agent.onMuteToggle(({ muted }) => dispatch({ type: 'SET_MUTED', value: muted }));
      // Incident 2026-09-14 fix 2: every Streams softphone error (microphone,
      // ICE, signalling, WebRTC, busy…) reaches the banner. With the framed
      // softphone these are raised inside the CCP iframe and relayed to this
      // bus, so this is the ONLY place the console can see them.
      agent.onSoftphoneError(err => reportSoftphone(err?.errorType, err));

      // ── D21 FIX (defect log 2026-08-26) ──────────────────────────────────
      // A TASK created by StartTaskContact rang for 23s and never raised an
      // accept screen: neither contact.onIncoming nor contact.onConnecting
      // fired for it, so incomingContact was never set and App.jsx rendered
      // no <IncomingCall />. Confirmed by devtools: agent.getContacts()
      // reported the contact correctly the whole time — Streams knew, our
      // React state did not. Every real scheduled callback is created the same
      // way (knktr-callback-booking -> StartTaskContact), so this made the
      // callback product unanswerable from the console.
      //
      // The lesson generalises past this one bug, and is the same one D19
      // names: a push-only subscription that misses a single event stays wrong
      // forever, because nothing ever re-checks. onRefresh gives us a periodic
      // agent snapshot, so we reconcile against getContacts() rather than
      // trusting that every transition event arrived. The event path is left
      // untouched — it works for voice, and this only fills its gaps.
      agent.onRefresh(a => {
        // D27/D19 (2026-09-10): reconcile the status pill against the agent
        // snapshot on every refresh, so a dropped or missed STATE_CHANGE
        // self-heals within one refresh instead of waiting for a page load.
        try {
          dispatch({ type: 'AGENT_STATUS', status: a.getState()?.name, reconcile: true });
        } catch { /* snapshot not ready */ }
        let contacts = [];
        try { contacts = a.getContacts() || []; } catch { return; }
        const live = new Set();
        for (const c of contacts) {
          let id = null;
          let type = null;
          try { id = c.getContactId(); type = c.getStatus()?.type; } catch { continue; }
          if (!id) continue;
          live.add(id);
          // Wire handlers for anything the connect.contact() subscription
          // never handed us; idempotent via wiredRef.
          wireContact(c);
          if (type === 'incoming' || type === 'connecting') {
            // D37: a ringing outbound leg the subscription missed (e.g. a page
            // reload mid-dial) is dialling, not incoming.
            if (isOutboundContact(c)) markDialling(c); else announceFor(c);
          } else if (announcedRef.current.has(id)) {
            // Left the ringing states — drop the announce guard so a genuine
            // re-offer of the same contact can raise the screen again.
            announcedRef.current.delete(id);
          }
        }
        // D6/D19 (2026-09-09): surface anything sitting in missed/error. Connect
        // holds the agent non-routable on EVERY channel while such a contact
        // exists (measured 2026-09-08: 0/0/0 slots), and until now the console
        // kept showing Available / "Waiting for next contact" over the top of
        // it. The server-side sweeper clears these after STUCK_MINUTES; this
        // gives the agent the same control immediately.
        const stuck = [];
        for (const c of contacts) {
          let id = null; let type = null; let status = null; let since = null;
          try {
            id = c.getContactId(); type = c.getType(); status = c.getStatus()?.type;
            since = c.getStatus()?.timestamp ?? null;
          } catch { continue; }
          if (id && (status === 'missed' || status === 'error')) stuck.push({ id, type, status, since });
          // D32 (2026-09-14): a contact that ENDED and sits in after-call work
          // with its wrap-up unsaved holds the agent at 0 slots on every
          // channel exactly like a missed one. Surface it past the shared
          // threshold — with NO clear control (LeftRail): the only way out is
          // Save & Complete, or a person closing it from the CCP.
          if (id && status === 'ended') {
            const t = since instanceof Date ? since.getTime() : Number(since);
            const ageMin = Number.isFinite(t) && t > 0 ? (Date.now() - t) / 60000 : 0;
            if (ageMin >= ACW_ALERT_MINUTES) stuck.push({ id, type, status, since });
          }
        }
        const sig = stuck.map(x => `${x.id}:${x.status}`).join('|');
        if (sig !== stuckSigRef.current) {
          stuckSigRef.current = sig;
          dispatch({ type: 'STUCK_CONTACTS', list: stuck });
        }
        // Only announcedRef is pruned here (a contact that left the snapshot
        // is no longer ringing). wiredRef is pruned by contact.onDestroy in
        // wireContact — the earlier "never prune wiredRef" rule was wrong: it
        // blocked re-wiring a re-offered contact, whose subscriptions Streams
        // had already dropped, so Accept worked but the workspace never came.
        // Incident 2026-09-14 fix 1 follow-through: the console assumes ONE
        // contact. When a voice leg dialled FROM a callback task ends,
        // CONTACT_ENDED / ACW_COMPLETE clear the workspace while the task is
        // still connected — and a connected contact with no workspace has no
        // End control, which is how a zombie task would be born. Re-surface a
        // connected contact whenever nothing is shown and no gate is open.
        const st = stateRef.current;
        // D32: after a page reload mid-ACW, onACW does not re-fire for the
        // already-ended contact, so the console showed "Waiting for next
        // contact" over an agent Connect still holds. Re-raise the wrap-up
        // gate for an ended contact we are not already gating.
        if (!st.acwActive && !st.incomingContact) {
          const ended = contacts.find(c => { try { return c.getStatus()?.type === 'ended' && !autoWrapRef.current.has(c.getContactId()); } catch { return false; } });
          if (ended) {
            acwContactRef.current = ended;
            const notesKey = notesKeyFor(notesContactId(ended));
            try {
              dispatch({ type: 'CONTACT_ACW', notesKey, storedNotes: readNotes(notesKey), wrapContext: wrapContextFrom(ended) });
            } catch (e) { console.warn('restore wrap-up failed', e); }
          }
        }
        if (!st.display && !st.acwActive && !st.incomingContact) {
          const connected = contacts.find(c => { try { return c.getStatus()?.type === 'connected' && !autoWrapRef.current.has(c.getContactId()); } catch { return false; } });
          if (connected) {
            let channel = null; try { channel = mapChannel(connected.getType()).toLowerCase(); } catch { /* unknown */ }
            const notesKey = notesKeyFor(notesContactId(connected));   // D31: chain root, same rule as onConnected
            dispatch({ type: 'CONTACT_CONNECTED', contact: connected, channel, notesKey, storedNotes: readNotes(notesKey) });
            try { dispatch({ type: 'SET_DISPLAY', data: displayFromContact(connected, agentRef.current) }); } catch (e) { console.warn('restore display failed', e); }
          }
        }
        // 2026-10-13 (Code review): the model summary (aiSummaryFull) is written
        // in the background ~2 s after the hand-over and can land AFTER the agent
        // connected. onConnected reads attributes once, so pick it up here when
        // the live contact's summary text changes.
        if (st.display && st.contact) {
          try {
            const cur = st.contact;
            const fresh = contacts.find(c => { try { return c.getContactId() === cur.getContactId(); } catch { return false; } });
            const full = fresh ? displaySummary(fresh.getAttributes()?.aiSummaryFull?.value) : null;
            if (full && full !== st.display.aiSummary) dispatch({ type: 'SET_DISPLAY_SUMMARY', aiSummary: full });
          } catch { /* snapshot not ready */ }
        }
        for (const id of [...announcedRef.current]) if (!live.has(id)) announcedRef.current.delete(id);
      });
    });


    return () => {
      if (ccpContainerRef.current) ccpContainerRef.current.innerHTML = '';
    };
  }, []);

  const handleAccept = useCallback(() => {
    const { incomingContact } = state;
    if (!incomingContact || !incomingContact.contact) return;   // demo ring has no contact
    // ring-out (2026-09-23): an Accept that then errors is a fault, not a miss.
    try { acceptAttemptRef.current.add(incomingContact.contact.getContactId()); } catch { /* no id */ }
    incomingContact.contact.accept({
      success: () => {},
      // Incident 2026-09-14 fix 2: an Accept that Connect refuses used to be
      // a devtools-only line. It now reaches the banner + incoming screen.
      // F7 (2026-10-15): Streams can report a failed Accept while Connect
      // connects the call seconds later. Decide after a short grace window
      // (lib/callState.js acceptFailureVerdict): connected → no banner.
      failure: err => {
        console.error('Accept failed:', err);
        const detail = typeof err === 'string' ? { errorMessage: err } : err;
        const accepted = incomingContact.contact;
        setTimeout(() => {
          let status = null;
          try { status = accepted.getStatus()?.type ?? null; } catch { status = null; }
          if (acceptFailureVerdict(status) === 'late_success') {
            console.warn('[knktr softphone] accept_late: Accept reported failed, call connected', detail || '');
            rememberSoftphoneError(softphoneRecord('accept_late', detail));
            return;
          }
          reportSoftphone('accept_failed', detail);
        }, ACCEPT_FAIL_GRACE_MS);
      },
    });
  }, [state.incomingContact, reportSoftphone]);

  const handleDecline = useCallback(() => {
    const { incomingContact } = state;
    if (!incomingContact || !incomingContact.contact) return;   // demo ring has no contact
    // ring-out (2026-09-23, review): NOT marked as an intentional end. A
    // successful reject goes connecting -> rejected, which fires no ERROR in
    // Streams; the only ERROR that could follow a Decline is one Connect
    // refused, and that must be classified on its own merits (a ring-out
    // reads "missed", an early one banners), never hidden as "cancelled".
    // 2026-10-13 (Storm 12:27 "re-offer to another agetn at once"): a declined
    // TASK used to sit REJECTED on this agent, holding every channel slot
    // (inbound calls included) until the sweeper cleared it ~2 min later. Now,
    // once the reject lands, the server restricts the task to the other agents
    // for ~2 min and dismisses it from this agent; if it can't, the browser
    // clears it. Voice keeps today's behaviour.
    const declined = incomingContact.contact;
    const isTask = typeOfContact(declined) === 'task';
    incomingContact.contact.reject({
      success: () => {
        dispatch({ type: 'CONTACT_INCOMING_CLEARED' });
        if (!isTask) return;
        let agentArn = '';
        try { agentArn = agentRef.current?.getConfiguration?.()?.agentARN || ''; } catch { agentArn = ''; }
        declineAndReoffer({ contact: declined, agentArn, url: TRANSFER_PREPARE_URL, getHeaders: apiHeaders })
          .then(r => console.info('Decline re-offer:', r));
      },
      failure: err => console.error('Decline failed:', err),
    });
  }, [state.incomingContact]);

  const handleMute = useCallback(() => {
    const { agent, contact, isMuted } = state;
    if (!agent || !contact) return;
    // Streams 2.x: mute lives on the AGENT (agent.mute()/unmute(), no
    // callbacks; state arrives via agent.onMuteToggle). connection.mute()
    // does not exist — the previous wiring threw and the button did nothing.
    if (isMuted) {
      agent.unmute();
    } else {
      agent.mute();
    }
  }, [state.agent, state.contact, state.isMuted]);

  const handleHold = useCallback(() => {
    const { contact, isHeld } = state;
    if (!contact) return;
    // Streams 2.x: hold/resume live on the CONNECTION being held — the
    // customer's initial connection — not on the contact (contact.hold does
    // not exist; the previous wiring threw and the button did nothing).
    const customerConn = contact.getInitialConnection();
    if (!customerConn) return;
    if (isHeld) {
      customerConn.resume({
        success: () => dispatch({ type: 'SET_HELD', value: false }),
        failure: err => console.error('Resume failed:', err),
      });
    } else {
      customerConn.hold({
        success: () => dispatch({ type: 'SET_HELD', value: true }),
        failure: err => console.error('Hold failed:', err),
      });
    }
  }, [state.contact, state.isHeld]);

  const handleEnd = useCallback(() => {
    // D7 (defect log 2026-08-26). Tasks and voice end the SAME way: destroy
    // the agent's connection. The 2026-09-01 task branch (contact.clear())
    // was rejected live by Connect — "Cannot clear contact: invalid state
    // TALKING" — because clear() completes a contact already in ACW; it does
    // not end a connected one. And the original "destroy() silently fails on
    // tasks" report was D21: getAgentConnection() itself threw because
    // amazon-connect-taskjs was not loaded. With taskjs present (main.jsx),
    // destroy() moves the task to ACW and the wrap-up gate takes it from
    // there. Verified live 2026-09-08.
    if (!state.contact) {
      console.warn('End pressed but no active contact is registered (D7) — likely a desynced session. Use the backup CCP to end it.');
      return;
    }

    // Hang up by destroying the AGENT'S connection (voice and task alike).
    // contact.destroy() is deprecated (logs a warning, did nothing here).
    // ACW is auto-completed by the contact.onACW -> contact.clear()
    // subscription, so no clear() needed in this handler.
    // D37 (2026-09-16): while an outbound call is still ringing the contact is
    // in state as `dialling`, so End reaches this point; if the agent leg
    // cannot be destroyed yet, drop the customer (initial) leg instead.
    const contact = state.contact;
    // D39: remember that this end was ours before either destroy runs — the
    // ERROR can arrive before the success callback does.
    try { markIntentionalEnd(intentionalEndRef.current, contact.getContactId()); } catch { /* no id */ }
    lastEndPathRef.current = null;
    const endCustomerLeg = why => {
      let initial = null;
      try { initial = contact.getInitialConnection(); } catch { /* none */ }
      if (!initial) { console.error('End call failed and no customer leg to drop:', why); return; }
      initial.destroy({
        success: () => { lastEndPathRef.current = 'customer-leg'; console.info(JSON.stringify({ msg: 'end-call-path', path: 'customer-leg', after: String(why) })); },
        failure: err => { lastEndPathRef.current = 'failed'; console.error('End call failed on the customer leg too:', err); },
      });
    };
    let agentConn = null;
    try { agentConn = contact.getAgentConnection(); } catch { /* none */ }
    if (!agentConn) {
      if (state.dialling) { endCustomerLeg('no agent connection while dialling'); return; }
      console.warn('End pressed but contact has no agent connection (D7).');
      return;
    }
    agentConn.destroy({
      success: () => { lastEndPathRef.current = 'agent-connection'; console.info(JSON.stringify({ msg: 'end-call-path', path: 'agent-connection' })); },
      failure: err => {
        if (state.dialling) { endCustomerLeg(err); return; }
        lastEndPathRef.current = 'failed';
        console.error('End call failed:', err);
      },
    });
  }, [state.contact, state.dialling]);

  // Persist the ACW wrap-up, then — only on a successful save — clear() the
  // contact so it completes Connect-side. This is the whole gate: no save, no
  // complete. Returns nothing; UI reads wrapSaving/wrapError from state.
  // One wrap-up per callback (16 Oct): after the call dialled from a callback task is wrapped up,
  // end that task; its onACW above saves and completes it with the same outcome. If the task
  // cannot be ended, nothing changes: the agent ends it and wraps it up as before.
  const closeCallbackTask = (ctx, disposition, notes) => {
    if (!ctx?.callbackId || !endsCallbackTask(disposition)) return;   // Test ends the task too (22 Oct)
    let contacts = []; try { contacts = agentRef.current?.getContacts?.() || []; } catch { contacts = []; }
    const task = findCallbackTask(contacts, ctx.callbackId, ctx.contactId);
    if (!task) return;
    let tid = null; try { tid = task.getContactId(); } catch { tid = null; }
    if (!tid) return;
    autoWrapRef.current.set(tid, { voiceContactId: ctx.contactId, disposition, notes, started: false });
    const giveUp = err => { autoWrapRef.current.delete(tid); console.warn('[one wrap-up] could not end the task; the agent ends it as before', err); };
    try { task.getAgentConnection().destroy({ success: () => {}, failure: giveUp }); } catch (err) { giveUp(err); }
  };

  const submitWrapUp = useCallback(async ({ notes, disposition, clientActionRequired, callback, overrideChainWarning }) => {
    const ctx = state.wrapContext;
    if (!ctx) return;
    // Demo mode (row 234): the wrap-up screen is for looking at; nothing is saved.
    if (DEMO) {
      dispatch({ type: 'WRAP_ERROR', error: 'Demo mode: nothing is saved.' });
      return;
    }
    // Row 94: never save or complete a wrap-up over a live call or chat.
    const liveBlock = wrapUpBlockedReason({ callActive: state.callActive, dialling: state.dialling, activeChannel: state.activeChannel });
    if (liveBlock) {
      dispatch({ type: 'WRAP_ERROR', error: liveBlock });
      return;
    }
    if (!WRAPUP_URL) {
      dispatch({
        type: 'WRAP_ERROR',
        error:
          'Wrap-up endpoint not configured (VITE_WRAPUP_URL). Nothing was saved and the contact was not completed.',
      });
      return;
    }
    // D8/D8b (defect log 2026-08-26): callback/task legs can arrive without
    // tenantId/contactId. The backend will reject that save, so surface a
    // handled, actionable message instead of a raw HTTP 400 — and never mask
    // a missing tenant with 'unknown'.
    if (!ctx.contactId || !ctx.tenantId) {
      console.error('Wrap-up blocked: missing identifiers', ctx);
      dispatch({
        type: 'WRAP_ERROR',
        error:
          'This contact is missing its ID details (a known issue with callback tasks), so the wrap-up can\u2019t be saved from here. Close the contact from the backup CCP and note it in the defect log \u2014 these notes won\u2019t be saved.',
      });
      return;
    }
    dispatch({ type: 'WRAP_SAVING' });
    const payload = {
      tenantId: ctx.tenantId,
      contactId: ctx.contactId,
      // D31: chain root of a re-offered callback (null otherwise). Not read by
      // the API today; carried so the row can be linked back to its chain later.
      initialContactId: ctx.initialContactId ?? null,
      brandName: ctx.brandName ?? null,
      aiSummary: ctx.aiSummary ?? null,
      notes: notes ?? '',
      disposition,
      clientActionRequired: !!clientActionRequired,
      // getConfiguration().username is the agent's Connect login.
      agentUsername: agentRef.current?.getConfiguration?.()?.username ?? 'unknown',
      // 2026-09-17: agent-scheduled callback. Only sent for the one disposition
      // that means anything to the Lambda; the picker enforces it's present
      // before Save is even enabled for that disposition.
      ...(disposition === 'Callback scheduled' && callback ? { callback } : {}),
      // #24 (2026-09-23): the agent explicitly confirmed booking #3+ in an
      // existing chain. Absent unless that confirm step ran — the server
      // gate is the real boundary, this only lets a deliberate override through.
      ...(disposition === 'Callback scheduled' && overrideChainWarning ? { overrideChainWarning: true } : {}),
      // 2026-09-17: connect-gap flag — separate from the above. Present
      // whenever this wrap-up follows a "Call now" dial from an EXISTING
      // scheduled callback (D8's dial-context fallback carries it), for
      // ANY disposition — the agent may have reached the person and
      // resolved it, in which case this just records a fast connect.
      // Was previously computed into wrapContext and silently dropped
      // before reaching the Lambda; see docs/ops/callback-ownership-
      // tasklist-investigation-2026-09-17.md.
      ...(ctx.callbackId ? { callbackId: ctx.callbackId } : {}),
    };
    try {
      const res = await fetch(WRAPUP_URL, {
        method: 'POST',
        headers: await apiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        // D8b: keep the raw server detail in the console for diagnosis, but
        // never render it to the agent — it reads as a broken system.
        const detail = await res.text().catch(() => '');
        console.error(`Wrap-up save failed (HTTP ${res.status})`, detail);
        throw new Error(
          'The wrap-up couldn\u2019t be saved \u2014 the server rejected it. Your notes are still here; try again, and if it keeps failing close the contact from the backup CCP and report it.'
        );
      }
      // 2026-09-16: surface the client-alert outcome (the save itself is done).
      const saved = await res.json().catch(() => ({}));
      dispatch({
        type: 'WRAP_ALERT',
        alert: saved?.clientActionRequired
          ? { sent: !!saved.alertSent, error: saved.alertError || null, recipient: saved.alertRecipient || null }
          : null,
      });
      // 2026-09-17: surface the agent-scheduled callback outcome, same
      // principle — the save is done regardless of what happened here.
      dispatch({
        type: 'WRAP_CALLBACK',
        callback: saved?.callbackBooked !== null && saved?.callbackBooked !== undefined
          ? { booked: !!saved.callbackBooked, windowText: saved.callbackWindowText || null,
             callbackId: saved.callbackId || null, error: saved.callbackError || null,
             // #24: present whenever the callback codepath ran at all;
             // requiresOverride true only on the gated-refusal branch.
             chainDepth: saved.callbackChainDepth ?? null, requiresOverride: !!saved.callbackRequiresOverride }
          : null,
      });
      // Persisted. Complete the contact Connect-side now, not before.
      const acwContact = acwContactRef.current;
      if (!acwContact) {
        removeNotes(notesKeyRef.current);
        dispatch({ type: 'ACW_COMPLETE' });
        return;
      }
      acwContact.clear({
        success: () => {
          acwContactRef.current = null;
          removeNotes(notesKeyRef.current);   // D28: saved — drop the mirror
          dispatch({ type: 'ACW_COMPLETE' });
          closeCallbackTask(ctx, disposition, notes);   // one wrap-up per callback (16 Oct)
        },
        failure: err => {
          console.error('ACW clear failed after successful save:', err);
          // 22 Oct: if Connect has already finished this contact, the save was all that was
          // needed. Leave the wrap-up screen instead of asking for a retry that can never work.
          let stillThere = true;
          try { stillThere = contactStillPresent(agentRef.current?.getContacts?.() || [], acwContact.getContactId()); } catch { stillThere = true; }
          if (!stillThere) {
            acwContactRef.current = null;
            removeNotes(notesKeyRef.current);
            dispatch({ type: 'ACW_COMPLETE' });
            closeCallbackTask(ctx, disposition, notes);
            return;
          }
          dispatch({
            type: 'WRAP_ERROR',
            error:
              'Saved, but Connect did not complete the contact. Click Save & Complete again to retry.',
          });
        },
      });
    } catch (err) {
      dispatch({
        type: 'WRAP_ERROR',
        error: err?.message || 'Save failed — check your connection and try again.',
      });
    }
  }, [state.wrapContext, state.callActive, state.dialling, state.activeChannel]);

  // D6/D19: clear a missed/error contact from the agent's session. Streams'
  // contact.clear() is the same call the native CCP's "Clear contact" makes.
  // Clearing a missed task re-offers it (observed 2026-09-08) — with D21
  // fixed that re-offer now renders an Accept screen, which is the point.
  const handleClearStuck = useCallback(id => {
    const agent = agentRef.current;
    if (!agent || !id) return;
    // D32: an ended contact in after-call work is never cleared from here —
    // that would complete it without its wrap-up. The rail shows no control
    // for it; this guard makes the rule hold even if one is added by mistake.
    if ((state.stuckContacts || []).some(s => s.id === id && s.status === 'ended')) {
      console.warn('Refusing to clear an unsaved wrap-up (D32): save it on the wrap-up screen instead.', id);
      return;
    }
    let target = null;
    try { target = (agent.getContacts() || []).find(c => c.getContactId() === id) || null; } catch { target = null; }
    if (!target) {
      console.warn('Clear pressed for a contact Streams no longer reports (D6):', id);
      dispatch({ type: 'STUCK_CONTACTS', list: state.stuckContacts.filter(s => s.id !== id) });
      return;
    }
    // 2026-09-23: a refused clear used to be a devtools-only line, so the agent
    // pressing the button learned nothing. It now reaches the banner.
    try {
      target.clear({
        success: () => {},
        failure: err => {
          console.error('Clear stuck contact failed:', err);
          reportSoftphone('clear_failed', typeof err === 'string' ? { errorMessage: err } : err);
        },
      });
    } catch (err) {
      console.error('Clear stuck contact threw:', err);
      reportSoftphone('clear_failed', err);
    }
  }, [state.stuckContacts, reportSoftphone]);

  // Row 200: the short note an agent gives for Meeting / Training. Kept here
  // for the status log (row 200 step 3, not built yet); cleared on any status
  // change without a note.
  const [statusNote, setStatusNote] = React.useState(null);
  const handleSetAgentState = useCallback((name, note = null) => {
    const { agent } = state;
    if (!agent) return;
    setStatusNote(note ? { status: name, note, at: Date.now() } : null);
    const target = agent.getAgentStates().find(s => s.name === name);
    if (!target) {
      console.error(`Agent state "${name}" not found on this routing profile`);
      dispatch({ type: 'AGENT_STATUS_ERROR', error: `"${name}" is not a state on this routing profile.` });
      return;
    }
    // D27: this control was never optimistic — it asks Connect and waits for
    // STATE_CHANGE. Now it also shows the request as pending, surfaces a
    // refusal beside the pill, and after STATUS_SETTLE_MS reads the snapshot
    // itself so a request Connect accepted but never echoed still settles.
    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    dispatch({ type: 'AGENT_STATUS_PENDING', name });
    agent.setState(target, {
      success: () => {},
      failure: err => {
        console.error('setState failed:', err);
        dispatch({
          type: 'AGENT_STATUS_ERROR',
          error: `Connect refused ${name}: ${describeStreamsError(err)}`,
        });
      },
    });
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      let current = null;
      try { current = agent.getState()?.name ?? null; } catch { /* keep null */ }
      dispatch({ type: 'AGENT_STATUS_SETTLE', status: current, requested: name });
    }, STATUS_SETTLE_MS);
  }, [state.agent]);

  // D28: the live-call notes setter, shared by RightRail (during the call)
  // and WrapUp (in ACW) so both edit the same text.
  const setCallNotes = useCallback(text => dispatch({ type: 'SET_CALL_NOTES', text }), []);

  // Agent-initiated outbound calls are prepared server-side before Streams is
  // allowed to dial. The backend validates the Cognito tenant membership and
  // Connect-user email, then stores a short-lived intent for this exact agent
  // ARN + destination. The outbound whisper flow consumes that intent and
  // persists the registry-owned tenant attributes before its first prompt.
  const handleDialNumber = useCallback(async (number, dialContext = null) => {
    const { agent, callActive, activeChannel, dialPending, dialling } = state;
    // Incident 2026-09-14 fix 1: only a live VOICE leg blocks a dial. A
    // connected TASK (the scheduled callback itself) must not — Connect's
    // routing profile allows voice 1 alongside task 5 with cross-channel
    // routing, and the task is exactly the thing the dial is for.
    // D37: a ringing outbound call is busy too (lib/callState.js).
    if (!agent || !number) return;
    const blocked = dialBlockedReason({ callActive, activeChannel, dialPending, dialling });
    if (blocked) {
      dispatch({ type: 'DIAL_ERROR', error: blocked });
      return;
    }
    const tenantId = String(dialContext?.tenantId || '').trim();
    if (!tenantId) {
      dispatch({ type: 'DIAL_ERROR', error: 'Choose a tenant before placing the call.' });
      return;
    }
    if (!OUTBOUND_CONTEXT_URL) {
      dispatch({ type: 'DIAL_ERROR', error: 'Outbound tenant binding is not configured yet.' });
      return;
    }
    const endpoint = window.connect?.Endpoint?.byPhoneNumber?.(number);
    if (!endpoint) {
      dispatch({ type: 'DIAL_ERROR', error: 'That phone number cannot be dialled.' });
      return;
    }
    let connectAgentArn = '';
    try { connectAgentArn = agent.getConfiguration()?.agentARN || ''; } catch { /* handled below */ }
    if (!connectAgentArn) {
      dispatch({ type: 'DIAL_ERROR', error: 'Your Amazon Connect identity is unavailable. Sign in again.' });
      return;
    }

    dispatch({ type: 'DIAL_PENDING' });
    try {
      const res = await fetch(OUTBOUND_CONTEXT_URL, {
        method: 'POST',
        headers: await apiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          tenantId, destinationNumber: number, connectAgentArn,
          // #20: the callback this dial is for, so the bind can stamp the link
          // on the contact (history + reload). Optional; the Lambda validates.
          ...(dialContext?.callbackId ? { callbackId: dialContext.callbackId } : {}),
          ...(dialContext?.originalContactId ? { originalContactId: dialContext.originalContactId } : {}),
        }),
      });
      const prepared = await res.json().catch(() => ({}));
      if (!res.ok || !prepared.ok) {
        throw new Error(prepared.error || `Outbound preparation failed (HTTP ${res.status})`);
      }

      // Retain the local copy only as an ACW fallback. The live display and
      // copilot use the attributes written by the outbound whisper flow.
      pendingDialContextRef.current = {
        ...dialContext,
        tenantId,
        brandName: prepared.brandName || dialContext?.brandName || null,
        // #20: the prepare's requestId; the flow writes it on the contact as
        // outboundContextId, and only a contact carrying it may use this ref.
        requestId: prepared.requestId || null,
      };
      agent.connect(endpoint, {
        queueARN: prepared.queueArn,
        success: () => dispatch({ type: 'DIAL_READY' }),
        failure: err => {
          pendingDialContextRef.current = null;
          console.error('Outbound dial failed:', err);
          dispatch({ type: 'DIAL_ERROR', error: `Amazon Connect could not place the call: ${describeStreamsError(err)}` });
        },
      });
    } catch (err) {
      pendingDialContextRef.current = null;
      dispatch({ type: 'DIAL_ERROR', error: err?.message || 'The outbound call could not be prepared.' });
    }
  }, [state.agent, state.callActive, state.activeChannel, state.dialPending, state.dialling]);

  // Row 1 Transfer, Phase 1 — second Streams call of a voice transfer: once
  // the target is on the call (addConnection succeeded), drop the AGENT'S OWN
  // leg so the customer is left alone with the transfer target. Isolated from
  // handleTransfer so the timeout/settled pattern is written once. A missing
  // agent connection (should not happen right after addConnection succeeded,
  // but defensive per D21's lesson about half-built contacts) is treated the
  // same as a destroy failure — the target is already connected either way.
  const destroyAgentLeg = useCallback(contact => {
    let settled = false;
    let agentConn = null;
    try { agentConn = contact.getAgentConnection(); } catch { /* none */ }
    if (!agentConn) {
      reportSoftphone('transfer_partial', { errorMessage: 'addConnection succeeded but no agent connection was found to destroy' });
      dispatch({ type: 'TRANSFER_ERROR', error: 'The transfer target connected, but your own leg could not be found to disconnect — check your call now.', partial: true, timedOut: false });
      return;
    }
    const destroyTimer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reportSoftphone('transfer_timeout', { errorMessage: `destroy() did not call back within ${TRANSFER_STEP_TIMEOUT_MS}ms — agent leg state unknown` });
      dispatch({ type: 'TRANSFER_ERROR', error: 'The transfer target connected, but Amazon Connect did not confirm your leg disconnected in time — check your call now.', partial: true, timedOut: true });
    }, TRANSFER_STEP_TIMEOUT_MS);
    agentConn.destroy({
      success: () => {
        if (settled) return;
        settled = true;
        clearTimeout(destroyTimer);
        dispatch({ type: 'TRANSFER_READY' });
      },
      failure: err => {
        if (settled) return;
        settled = true;
        clearTimeout(destroyTimer);
        reportSoftphone('transfer_partial', { errorMessage: describeStreamsError(err) });
        dispatch({ type: 'TRANSFER_ERROR', error: `The transfer target connected, but your own leg could not be disconnected: ${describeStreamsError(err)} — check your call now.`, partial: true, timedOut: false });
      },
    });
  }, [reportSoftphone]);

  // Row 1 Transfer, Phase 1 (2026-09-28, rebuilt): a live-call test found
  // contact.transfer() fails on voice with "channel type unsupported for this
  // operation" — confirmed against AWS's own TransferContact API reference
  // ("Transfer is only supported for TASK and EMAIL contacts"), ruling out
  // queue-association and tenant-entitlement as the cause (a genuinely valid,
  // associated queue target failed identically). The correct voice mechanism
  // (AWS blog) is addConnection() to bring the target on, then destroy the
  // agent's own connection (destroyAgentLeg above) — two independent Streams
  // calls opening/closing real legs mid-call, so a failure or a silent
  // timeout on EITHER one can leave the contact in a state this console
  // cannot fully know from here. See TRANSFER_ERROR's partial/timedOut
  // fields and the FRIENDLY transfer_partial/transfer_timeout messages in
  // lib/softphoneErrors.js for how that ambiguity is surfaced and held open.
  //
  // targetType is 'queue' | 'phone_number' ('agent' is validated server-side
  // but has no console UI yet, pending a live-call test of direct Endpoint
  // construction). target carries the type-specific field the Lambda expects
  // (destinationNumber for phone_number; queue needs none, it is always
  // resolved server-side to the contact's own tenant).
  //
  // The prepare call MUST happen first, every time, for every target type —
  // including queue and phone-number. addConnection() is only ever called
  // with the endpoint the Lambda returned, never a client-constructed one.
  const handleTransfer = useCallback(async (targetType, target = {}) => {
    const { agent, contact, callActive, dialling, transferPartial, transferTimedOut } = state;
    if (!agent || !contact) return;
    // A prior attempt on this contact left the connection state ambiguous —
    // refuse rather than stack an unverified second addConnection on top of
    // it. Only CONTACT_ENDED clears this (see the reducer).
    if (transferPartial) {
      dispatch({
        type: 'TRANSFER_ERROR',
        error: 'This call is in an unknown transfer state from an earlier attempt — check your call before trying again.',
        partial: true,
        timedOut: transferTimedOut,
      });
      return;
    }
    if (!callActive || dialling) {
      dispatch({ type: 'TRANSFER_ERROR', error: 'Finish connecting before transferring.' });
      return;
    }
    if (!TRANSFER_PREPARE_URL) {
      dispatch({ type: 'TRANSFER_ERROR', error: 'Transfer is not configured yet.' });
      return;
    }
    const contactId = contactIdForApi(contact);
    if (!contactId) {
      dispatch({ type: 'TRANSFER_ERROR', error: 'No active contact to transfer.' });
      return;
    }

    dispatch({ type: 'TRANSFER_PENDING' });
    let prepared;
    try {
      const res = await fetch(TRANSFER_PREPARE_URL, {
        method: 'POST',
        headers: await apiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ contactId, targetType, ...target }),
      });
      prepared = await res.json().catch(() => ({}));
      if (!res.ok || !prepared.ok) {
        throw new Error(prepared.error || `Transfer could not be prepared (HTTP ${res.status})`);
      }
    } catch (err) {
      dispatch({ type: 'TRANSFER_ERROR', error: err?.message || 'The transfer could not be prepared.' });
      return;
    }

    const endpoint = new window.connect.Endpoint(prepared.endpoint);
    let settled = false;
    const addTimer = setTimeout(() => {
      if (settled) return;
      settled = true;
      reportSoftphone('transfer_timeout', { errorMessage: `addConnection() did not call back within ${TRANSFER_STEP_TIMEOUT_MS}ms — target connection state unknown` });
      dispatch({ type: 'TRANSFER_ERROR', error: 'Amazon Connect did not confirm the transfer target connected in time — check your call now.', partial: true, timedOut: true });
    }, TRANSFER_STEP_TIMEOUT_MS);
    contact.addConnection(endpoint, {
      success: () => {
        if (settled) return;
        settled = true;
        clearTimeout(addTimer);
        destroyAgentLeg(contact);
      },
      failure: err => {
        if (settled) return;
        settled = true;
        clearTimeout(addTimer);
        // A genuine failure callback, not a timeout — the target never
        // connected, so this is a clean no-op, not a partial state.
        dispatch({ type: 'TRANSFER_ERROR', error: `Amazon Connect could not connect the transfer target: ${describeStreamsError(err)}`, partial: false, timedOut: false });
      },
    });
  }, [state.agent, state.contact, state.callActive, state.dialling, state.transferPartial, state.transferTimedOut, reportSoftphone, destroyAgentLeg]);

  // Amazon Connect sign-in (2026-10-21, Storm: no second tab left open).
  // History: on 2026-08-31 the hidden CCP iframe was found to die before any
  // login (it mounts with no session, exhausts its ACK/SYN retries), so the
  // old button opened the CCP in our own popup and reloaded the page once the
  // agent closed it, which left a second live CCP open until they did.
  // Now Streams' own sign-in runs on the click: it refreshes the iframe until
  // acknowledged and closes its login window itself. Every failure path falls
  // back to the old popup + reload. Rules and safety nets: lib/connectLogin.js.
  const agentConnectedRef = useRef(false);
  agentConnectedRef.current = Boolean(state.agentConnected);
  const handleLogin = useCallback(() => {
    startConnectLogin({
      connectApi: window.connect,
      ccpUrl: CCP_URL,
      isConnected: () => agentConnectedRef.current,
    });
  }, []);

  // Left-rail navigation: which idle view is shown (voice | chat | task).
  // A live contact always seizes the centre regardless of this selection.
  const [activeTab, setActiveTab] = React.useState(DEMO_SCREEN === 'task' ? 'task' : 'voice');

  // Row 1 Transfer, Phase 1: the picker's open/closed state, same local-state
  // shape as activeTab above. Closed automatically once there is no live
  // contact to transfer, so it never lingers open onto the next call.
  const [transferPanelOpen, setTransferPanelOpen] = React.useState(false);
  useEffect(() => { if (!state.contact) setTransferPanelOpen(false); }, [state.contact]);

  return (
    <ContactContext.Provider
      value={{
        ...state,
        isDemo: DEMO,
        activeTab,
        setActiveTab,
        transferPanelOpen,
        setTransferPanelOpen,
        ccpContainerRef,
        handleAccept,
        handleDecline,
        handleMute,
        handleHold,
        handleEnd,
        handleSetAgentState,
        statusNote,
        handleClearStuck,
        handleDialNumber,
        handleTransfer,
        handleLogin,
        submitWrapUp,
        setCallNotes,
        wrapNotesAiSeeded: state.wrapNotesAiSeeded,
        // Incident 2026-09-14: fix 1 (dial gate is voice-only) + fix 2 (banner).
        voiceActive: (state.callActive || state.dialling) && state.activeChannel === 'voice',   // D37: ringing counts
        // Row 94: the reason a waiting wrap-up is held back (live call or chat), or null.
        wrapUpBlocked: wrapUpBlockedReason({ callActive: state.callActive, dialling: state.dialling, activeChannel: state.activeChannel }),
        clearSoftphoneError,
        clearCallCancelled,
        clearCallMissed,
        // Option B (2026-09-23, vault #233): the agent's Connect login, read-only,
        // so the callbacks list can tell "Assigned to you" from another agent.
        // The same value the wrap-up save already sends (agentUsername).
        agentUsername: (() => { try { return state.agent?.getConfiguration?.()?.username ?? null; } catch { return null; } })(),
      }}
    >
      {children}
    </ContactContext.Provider>
  );
}

export const useContact = () => useContext(ContactContext);
